"use client";

import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "@workspace/ui/components/popover";
import { VisuallyHidden } from "@workspace/ui/components/visually-hidden";
import {
  type MouseEvent,
  useCallback,
  useState,
  useSyncExternalStore,
} from "react";
import { MarketplaceJobDetailsCard } from "./marketplace-job-details-card";
import { MarketplaceJobMarkerLabel } from "./marketplace-job-marker-label";
import type { MarketplaceJob } from "./use-cluster";

function subscribeHeight(
  element: HTMLDivElement | null,
  callback: () => void
): () => void {
  if (!element) {
    return () => null;
  }
  const observer = new ResizeObserver(callback);
  observer.observe(element);
  return () => observer.disconnect();
}

function getHeightSnapshot(element: HTMLDivElement | null): number {
  return element?.offsetHeight ?? 0;
}

const FALLBACK_HEIGHT = 420;

export function MarketplaceJobPopup({
  job,
  isOpen,
  onActivate,
  onOpenChange,
  isActive,
  isHovered,
}: {
  job: MarketplaceJob;
  isOpen?: boolean;
  onActivate?: () => void;
  onOpenChange?: (open: boolean) => void;
  /** Phase 5: marker highlight when its row is selected/hovered. */
  isActive?: boolean;
  isHovered?: boolean;
}) {
  const [internalOpen, setInternalOpen] = useState(false);
  const [contentElement, setContentElement] = useState<HTMLDivElement | null>(
    null
  );

  const controlled = onOpenChange !== undefined;
  const open = controlled ? (isOpen ?? false) : internalOpen;

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (controlled) {
        if (!nextOpen) {
          onOpenChange?.(false);
        }
        return;
      }
      setInternalOpen(nextOpen);
    },
    [controlled, onOpenChange]
  );

  const handleLabelActivate = useCallback(
    (event: MouseEvent<HTMLSpanElement>) => {
      event.stopPropagation();
      onActivate?.();
    },
    [onActivate]
  );

  const subscribe = useCallback(
    (callback: () => void) => subscribeHeight(contentElement, callback),
    [contentElement]
  );
  const getSnapshot = useCallback(
    () => getHeightSnapshot(contentElement),
    [contentElement]
  );
  const measuredHeight = useSyncExternalStore(subscribe, getSnapshot, () => 0);

  const sideOffset = -Math.round((measuredHeight || FALLBACK_HEIGHT) / 2);

  return (
    <Popover onOpenChange={handleOpenChange} open={open}>
      <PopoverAnchor asChild>
        <MarketplaceJobMarkerLabel
          isActive={isActive}
          isHovered={isHovered}
          onClick={handleLabelActivate}
          onPointerDown={(event) => {
            event.stopPropagation();
          }}
          title={job.title}
        />
      </PopoverAnchor>
      <PopoverContent
        className="m-0 w-[22rem] border-none bg-transparent p-0 shadow-none lg:w-[25rem] xl:w-[26rem]"
        onInteractOutside={(event) => {
          event.preventDefault();
        }}
        onPointerDownOutside={(event) => {
          event.preventDefault();
        }}
        sideOffset={sideOffset}
      >
        <div ref={setContentElement}>
          <VisuallyHidden>
            <p>Job details for {job.title}</p>
          </VisuallyHidden>
          <MarketplaceJobDetailsCard job={job} />
        </div>
      </PopoverContent>
    </Popover>
  );
}
